import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPut, apiPost, apiDelete } from "../../../api";
import { DEGREES } from "../../../data/degrees";
import { MAJORS } from "../../../data/majors";
import "../styles/AccountSettings.css";

// NEW: First-step confirmation modal
function PreConfirmationModal({ onConfirm, onCancel }) {
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-content confirmation-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-header">Are you sure?</h3>
        <p>This action is **irreversible**. Are you sure you want to proceed with deleting your account?</p>
        <div className="modal-actions">
          <button onClick={onCancel} className="btn btn-secondary">Cancel</button>
          <button onClick={onConfirm} className="btn btn-danger">Yes, I'm Sure</button>
        </div>
      </div>
    </div>
  );
}

// Final confirmation modal
function DeleteConfirmationModal({ onConfirm, onCancel }) {
  const [confirmationText, setConfirmationText] = useState("");
  const requiredText = "DELETE ACCOUNT";
  const isConfirmed = confirmationText === requiredText;

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-content confirmation-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-header">Final Confirmation</h3>
        <p>This is the final step. All your data will be permanently deleted.</p>
        <p>To confirm, please type "<strong>{requiredText}</strong>" in the box below:</p>
        <input
          className="form-input"
          type="text"
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          placeholder={requiredText}
          autoComplete="off"
        />
        <div className="modal-actions">
          <button onClick={onCancel} className="btn btn-secondary">Cancel</button>
          <button onClick={onConfirm} className="btn btn-danger" disabled={!isConfirmed}>
            Permanently Delete Account
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AccountSettings() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({});
  const [changed, setChanged] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  // UPDATED: This state now controls the multi-step modal process
  const [deleteStep, setDeleteStep] = useState(0); // 0: none, 1: pre-confirm, 2: final-confirm

  useEffect(() => {
    apiGet("/auth/me")
      .then((res) => {
        setUser(res.user);
        const [firstName = "", lastName = ""] = res.user?.name?.split(" ") || [];
        setForm({
          firstName,
          lastName,
          degree: res.user?.degree || "",
          major: res.user?.major || "",
          yearOfStudy: res.user?.yearOfStudy || "",
          preferredPlatform: res.user?.preferredPlatform || "",
          discordHandle: res.user?.discordHandle || "",
          instagramHandle: res.user?.instagramHandle || "",
        });
      })
      .catch(() => setMsg({ text: "Error fetching account info", type: "error" }));
  }, []);

  const updateForm = (key, val) => {
    setForm({ ...form, [key]: val });
    setChanged(true);
    setMsg({ text: "", type: "" });
  };

  const saveChanges = async () => {
    try {
      const data = await apiPut("/account", form);
      setUser(data.user);
      setChanged(false);
      setMsg({ text: "Changes saved successfully.", type: "success" });
    } catch (err) {
      setMsg({ text: err.message, type: "error" });
    }
  };

  const changePassword = async () => {
    if (newPwd !== confirmPwd) {
      setMsg({ text: "New passwords do not match.", type: "error" });
      return;
    }
    try {
      await apiPost("/account/change-password", { oldPassword: oldPwd, newPassword: newPwd });
      setMsg({ text: "Password updated successfully.", type: "success" });
      setOldPwd("");
      setNewPwd("");
      setConfirmPwd("");
      setShowPwd(false);
    } catch (err) {
      setMsg({ text: err.message, type: "error" });
    }
  };

  // Handler for the account deletion API call
  const handleDeleteAccount = async () => {
    try {
      await apiDelete("/account");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      nav("/auth/react/signin");
    } catch (err) {
      console.error("Failed to delete account:", err.message);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      nav("/auth/react/signin");
    }
  };

  return (
    <div className="account-settings-container page-card">
      <h2>Account Settings</h2>

      {/* --- Personal Information --- */}
      <div className="form-section">
        <h3>Personal Information</h3>
        <div className="form-row">
          <label className="form-label">Email</label>
          <input className="form-input disabled" value={user?.email || ""} disabled />
        </div>
        <div className="form-row">
          <label className="form-label">First Name <span className="required-star">*</span></label>
          <input className="form-input" value={form.firstName} onChange={(e) => updateForm("firstName", e.target.value)} />
        </div>
        <div className="form-row">
          <label className="form-label">Last Name <span className="required-star">*</span></label>
          <input className="form-input" value={form.lastName} onChange={(e) => updateForm("lastName", e.target.value)} />
        </div>
      </div>

      {/* --- Academic Information --- */}
      <div className="form-section">
        <h3>Academic Information</h3>
        <div className="form-row">
          <label className="form-label">Degree</label>
          <select className="form-select" value={form.degree} onChange={(e) => updateForm("degree", e.target.value)}>
            <option value="">-- Select Degree --</option>
            {DEGREES.map((d) => (<option key={d.code} value={d.code}>{d.label}</option>))}
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Major</label>
          <select className="form-select" value={form.major} onChange={(e) => updateForm("major", e.target.value)}>
            <option value="">-- Select Major --</option>
            {MAJORS.map((m) => (<option key={m} value={m}>{m}</option>))}
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Year of Study</label>
          <input className="form-input" value={form.yearOfStudy} onChange={(e) => updateForm("yearOfStudy", e.target.value)} />
        </div>
      </div>

      {/* --- Connection Settings --- */}
      <div className="form-section">
        <h3>Connection Settings</h3>
        <div className="form-row">
          <label className="form-label">Preferred Platform <span className="required-star">*</span></label>
          <select className="form-select" value={form.preferredPlatform || ""} onChange={(e) => updateForm("preferredPlatform", e.target.value)}>
            <option value="">-- Select --</option>
            <option value="DISCORD">Discord</option>
            <option value="INSTAGRAM">Instagram</option>
          </select>
        </div>
        {form.preferredPlatform === "DISCORD" && (
          <div className="form-row">
            <label className="form-label">Discord Handle <span className="required-star">*</span></label>
            <input className="form-input" value={form.discordHandle || ""} onChange={(e) => updateForm("discordHandle", e.target.value)} />
          </div>
        )}
        {form.preferredPlatform === "INSTAGRAM" && (
          <div className="form-row">
            <label className="form-label">Instagram Handle <span className="required-star">*</span></label>
            <input className="form-input" value={form.instagramHandle || ""} onChange={(e) => updateForm("instagramHandle", e.target.value)} />
          </div>
        )}
      </div>

      {msg.text && <div className={`status-message ${msg.type}`}>{msg.text}</div>}

      <div className="action-buttons">
        <button className="btn btn-primary" disabled={!changed} onClick={saveChanges}>
          Save Changes
        </button>
      </div>

      <hr className="divider" />

      {/* UPDATED: Danger Zone buttons */}
      <div className="danger-zone">
        <button className="btn btn-secondary" onClick={() => setShowPwd(!showPwd)}>
          {showPwd ? "Cancel Password Change" : "Change Password"}
        </button>
        <button className="btn btn-danger-outline" onClick={() => setDeleteStep(1)}>
          Delete Account
        </button>
      </div>

      {showPwd && (
        <div className="password-section">
          <div className="form-row">
            <label className="form-label">Old Password</label>
            <input className="form-input" type="password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">New Password</label>
            <input className="form-input" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Confirm New Password</label>
            <input className="form-input" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} />
          </div>
          <div className="action-buttons">
            <button className="btn btn-primary" onClick={changePassword}>Save New Password</button>
          </div>
        </div>
      )}

      {/* UPDATED: Conditionally render the correct modal based on the step */}
      {deleteStep === 1 && (
        <PreConfirmationModal
          onCancel={() => setDeleteStep(0)}
          onConfirm={() => setDeleteStep(2)} // Proceeds to the final step
        />
      )}
      {deleteStep === 2 && (
        <DeleteConfirmationModal
          onCancel={() => setDeleteStep(0)} // Resets the process entirely
          onConfirm={handleDeleteAccount}
        />
      )}
    </div>
  );
}